import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShipmentService, shipmentJobStorage } from "src/services/shipments";
import { queryKeys } from "src/constants";
import { ShipmentJobRecord, ShipmentJobStatus } from "src/types/api/shipments/shipmentJob";

// Polling cadence — fast at the start (most jobs finish within a few seconds),
// back off to 4s after the first minute, and give up at 10 minutes.
const FAST_POLL_MS = 1500;
const SLOW_POLL_MS = 4000;
const SLOW_AFTER_MS = 60 * 1000;
const GIVE_UP_AFTER_MS = 10 * 60 * 1000;

const TERMINAL_STATUSES: ShipmentJobStatus[] = ["SUCCEEDED", "FAILED"];

export interface UseShipmentJobResult {
  job: ShipmentJobRecord | undefined;
  isPolling: boolean;
  isTerminal: boolean;
  hasTimedOut: boolean;
  fetchError: unknown;
}

const useShipmentJob = (shipmentService: ShipmentService, jobId: string | undefined): UseShipmentJobResult => {
  // Reset the polling clock when jobId changes — Date.now() doesn't reference
  // jobId, so the exhaustive-deps lint can't see the intent.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const startedAt = useMemo(() => Date.now(), [jobId]);

  const query = useQuery({
    queryKey: queryKeys.shipmentJob(jobId),
    queryFn: () => {
      if (!jobId) throw new Error("jobId required");
      return shipmentService.getShipmentJob(jobId);
    },
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const data = query.state.data as ShipmentJobRecord | undefined;
      if (data && TERMINAL_STATUSES.includes(data.status)) return false;
      if (Date.now() - startedAt > GIVE_UP_AFTER_MS) return false;
      return Date.now() - startedAt > SLOW_AFTER_MS ? SLOW_POLL_MS : FAST_POLL_MS;
    },
    // Keep polling while the user looks at the page, even on background tabs —
    // most jobs are short so we'd rather catch completion in the background.
    refetchIntervalInBackground: true
  });

  const job = query.data;
  const isTerminal = !!job && TERMINAL_STATUSES.includes(job.status);
  const hasTimedOut = !isTerminal && Date.now() - startedAt > GIVE_UP_AFTER_MS;

  // On terminal state, drop the localStorage entry so future page loads don't
  // try to resume a finished job.
  useEffect(() => {
    if (jobId && isTerminal) shipmentJobStorage.remove(jobId);
  }, [jobId, isTerminal]);

  return {
    job,
    isPolling: query.isFetching && !isTerminal,
    isTerminal,
    hasTimedOut,
    fetchError: query.error
  };
};

export { useShipmentJob };
